import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faFileAlt,
  faSave,
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import ModalOverlay from '../../../components/ui/ModalOverlay/ModalOverlay';
import DatePicker from '../../../components/ui/DatePicker/DatePicker';
import s from '../CaseFilePage.module.css';

export type MissingField = {
  fieldName: string;
  displayName: string;
  type: string;
  dbTable?: string;
  dbColumn?: string;
  editable?: boolean;
  required?: boolean;
  options?: { value: string; label: string }[] | null;
};

export type ScanData = {
  templateName: string;
  fileType: string;
  templateFile?: string;
  fieldData: Record<string, string>;
  missingFields: MissingField[];
  totalFields: number;
};

interface FieldValidationModalProps {
  fieldValidationPhase: 'scanning' | 'ready' | 'missing';
  scanProgress: number;
  scanData: ScanData | null;
  missingFieldValues: Record<string, string>;
  setMissingFieldValues: React.Dispatch<React.SetStateAction<Record<string, string>>>;
  draftGenerating: boolean;
  fieldSaving: boolean;
  handleFieldValidationContinue: () => void;
  handleFieldValidationSaveAndContinue: () => void;
  onClose: () => void;
}

export default function FieldValidationModal({
  fieldValidationPhase,
  scanProgress,
  scanData,
  missingFieldValues,
  setMissingFieldValues,
  draftGenerating,
  fieldSaving,
  handleFieldValidationContinue,
  handleFieldValidationSaveAndContinue,
  onClose,
}: FieldValidationModalProps) {
  return (
    <ModalOverlay
      onClose={onClose}
      title={
        fieldValidationPhase === 'scanning'
          ? 'Scanning Document Fields...'
          : fieldValidationPhase === 'ready'
            ? 'Ready to Generate'
            : 'Missing Field Data'
      }
    >
      <div className={s.modalForm}>
        {/* Scanning phase */}
        {fieldValidationPhase === 'scanning' && (
          <div className={s.fieldScanView}>
            <div className={s.progressBarTrack}>
              <div className={s.progressBarFill} style={{ width: `${scanProgress}%` }} />
            </div>
            <p className={s.scanStatusText}>
              {scanProgress < 30
                ? 'Connecting to server...'
                : scanProgress < 80
                  ? 'Scanning template fields...'
                  : scanProgress < 100
                    ? 'Analyzing field data...'
                    : 'Generating document...'}
            </p>
          </div>
        )}

        {/* All fields available */}
        {fieldValidationPhase === 'ready' && (
          <div className={s.fieldScanView}>
            <div style={{ textAlign: 'center', padding: '16px 0' }}>
              <FontAwesomeIcon
                icon={faCheckCircle}
                style={{ color: 'var(--green-500, #22c55e)', fontSize: '2rem' }}
              />
              <p style={{ marginTop: 8 }}>All fields populated — generating document...</p>
            </div>
          </div>
        )}

        {/* Missing fields — user can fill in values */}
        {fieldValidationPhase === 'missing' && scanData && (
          <>
            <div className={s.missingFieldsBanner}>
              <FontAwesomeIcon icon={faExclamationTriangle} />{' '}
              <strong>{scanData.missingFields.length}</strong> of {scanData.totalFields} fields are
              missing or empty. Fill them in below, then choose an action.
            </div>

            <div className={s.missingFieldsList}>
              {scanData.missingFields.map((field) => (
                <div className={s.formGroup} key={field.fieldName}>
                  <label>{field.displayName || field.fieldName.replace(/_/g, ' ')}</label>
                  {field.type === 'date' ? (
                    <DatePicker
                      value={missingFieldValues[field.fieldName] ?? ''}
                      onChange={(v) =>
                        setMissingFieldValues((prev) => ({ ...prev, [field.fieldName]: v }))
                      }
                    />
                  ) : field.type === 'dropdown' && field.options && field.options.length > 0 ? (
                    <select
                      value={missingFieldValues[field.fieldName] ?? ''}
                      onChange={(e) =>
                        setMissingFieldValues((prev) => ({
                          ...prev,
                          [field.fieldName]: e.target.value,
                        }))
                      }
                    >
                      <option value="">{`Select ${field.displayName || field.fieldName.replace(/_/g, ' ')}`}</option>
                      {field.options.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === 'boolean' ? (
                    <select
                      value={missingFieldValues[field.fieldName] ?? ''}
                      onChange={(e) =>
                        setMissingFieldValues((prev) => ({
                          ...prev,
                          [field.fieldName]: e.target.value,
                        }))
                      }
                    >
                      <option value="">Select…</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                  ) : field.type === 'number' ? (
                    <input
                      type="number"
                      placeholder={`Enter ${field.displayName || field.fieldName.replace(/_/g, ' ')}`}
                      value={missingFieldValues[field.fieldName] ?? ''}
                      onChange={(e) =>
                        setMissingFieldValues((prev) => ({
                          ...prev,
                          [field.fieldName]: e.target.value,
                        }))
                      }
                    />
                  ) : field.type === 'textarea' ? (
                    <textarea
                      placeholder={`Enter ${field.displayName || field.fieldName.replace(/_/g, ' ')}`}
                      value={missingFieldValues[field.fieldName] ?? ''}
                      onChange={(e) =>
                        setMissingFieldValues((prev) => ({
                          ...prev,
                          [field.fieldName]: e.target.value,
                        }))
                      }
                    />
                  ) : (
                    <input
                      type="text"
                      placeholder={`Enter ${field.displayName || field.fieldName.replace(/_/g, ' ')}`}
                      value={missingFieldValues[field.fieldName] ?? ''}
                      onChange={(e) =>
                        setMissingFieldValues((prev) => ({
                          ...prev,
                          [field.fieldName]: e.target.value,
                        }))
                      }
                    />
                  )}
                </div>
              ))}
            </div>

            <div className={s.modalActions}>
              <button className={s.btnSecondary} onClick={onClose}>
                Cancel
              </button>
              <button
                className={s.btnPrimary}
                onClick={handleFieldValidationContinue}
                disabled={draftGenerating}
              >
                {draftGenerating ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faFileAlt} />
                )}{' '}
                Continue
              </button>
              <button
                className={s.btnSave}
                onClick={handleFieldValidationSaveAndContinue}
                disabled={draftGenerating || fieldSaving}
              >
                {fieldSaving ? (
                  <FontAwesomeIcon icon={faSpinner} spin />
                ) : (
                  <FontAwesomeIcon icon={faSave} />
                )}{' '}
                Save &amp; Continue
              </button>
            </div>
          </>
        )}
      </div>
    </ModalOverlay>
  );
}
